import React, { useState, useEffect, useRef } from 'react';
import { DollarSign, Save, X, BookOpen, User, Clock, Calculator, Pencil, Calendar, History, Upload, FileText, Maximize2, Minimize2, Eye } from 'lucide-react';
import api from '../api';

const SalarySettings = ({ onClose, initialDiploId, userRole }) => {
  const [diplomaturas, setDiplomaturas] = useState([]);
  const [selectedDiploId, setSelectedDiploId] = useState(initialDiploId || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const [rates, setRates] = useState({
    monthly_coordinator_rate: 0,
    hourly_teacher_rate: 0
  });
  const [calculating, setCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  
  const [filters, setFilters] = useState({
    teacher_email: '',
    start_date: '',
    end_date: '',
    isManual: false,
    manualEmail: ''
  });
  const [allDocentes, setAllDocentes] = useState([]);

  const [activeTab, setActiveTab] = useState('config'); // 'config' or 'history'
  const [paymentModal, setPaymentModal] = useState(null); // { type: 'CO'|'DO', data: {} }
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // payment object to show full detail

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  useEffect(() => {
    fetchDiplomaturas();
  }, []);

  useEffect(() => {
    if (diplomaturas.length > 0 && selectedDiploId) {
      applyDiploRates(selectedDiploId, diplomaturas);
      setCalcResult(null); // Reset calculation when changing diplo
    }
  }, [diplomaturas, selectedDiploId]);

  const applyDiploRates = (id, list) => {
    const diplo = list.find(d => d.id.toString() === id.toString());
    if (diplo) {
      setRates({
        monthly_coordinator_rate: diplo.monthly_coordinator_rate || 0,
        hourly_teacher_rate: diplo.hourly_teacher_rate || 0
      });
    }
  };

  const fetchDiplomaturas = async () => {
    setLoading(true);
    try {
      const [diplosRes, usersRes] = await Promise.all([
        api.get('diplomas/'),
        api.get('users/')
      ]);
      setDiplomaturas(diplosRes.data);
      // All users except students are potential beneficiaries for viáticos
      setAllDocentes(usersRes.data.filter(u => u.role !== 'AL'));
    } catch (err) {
      console.error('SalarySettings: Error fetching data:', err);
      setMessage({ type: 'error', text: 'Error al cargar datos iniciales.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDiploChange = (e) => {
    const id = e.target.value;
    setSelectedDiploId(id);
    applyDiploRates(id, diplomaturas);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedDiploId) return;

    setSaving(true);
    setMessage('');
    try {
      await api.patch(`diplomas/${selectedDiploId}/`, rates);
      setMessage({ type: 'success', text: 'Valores actualizados correctamente' });
      setDiplomaturas(prev => prev.map(d => d.id.toString() === selectedDiploId ? { ...d, ...rates } : d));
    } catch (err) {
      console.error('Error updating salaries:', err);
      setMessage({ type: 'error', text: 'Error al actualizar los valores' });
    } finally {
      setSaving(false);
    }
  };

  const handleCalculateHours = async () => {
    if (!selectedDiploId) return;
    setCalculating(true);
    
    // Build query params
    const params = new URLSearchParams();
    const emailToUse = filters.isManual ? filters.manualEmail : filters.teacher_email;
    if (emailToUse) params.append('teacher_email', emailToUse);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    try {
      const res = await api.get(`diplomas/${selectedDiploId}/calculate_hours/?${params.toString()}`);
      setCalcResult(res.data);
    } catch (err) {
      console.error('Error calculating hours:', err);
      setMessage({ type: 'error', text: 'Error al calcular las horas' });
    } finally {
      setCalculating(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('payments/');
      setPaymentHistory(res.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePaymentSubmit = async (formData) => {
    // Validation ...
    const userId = formData.get('user');
    const diplomaId = formData.get('diploma');
    
    if (!userId || userId === 'null' || userId === 'undefined') {
      alert('Error: Debe seleccionar un destinatario válido (usuario no asignado).');
      return;
    }
    
    if (!diplomaId || diplomaId === 'null' || diplomaId === 'undefined') {
      alert('Error: Debe seleccionar una diplomatura válida.');
      return;
    }

    try {
      await api.post('payments/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage({ type: 'success', text: 'Pago registrado exitosamente' });
      setPaymentModal(null);
      if (activeTab === 'history') fetchHistory();
    } catch (err) {
      console.error('Error saving payment:', err);
      const errorDetail = err.response?.data ? JSON.stringify(err.response.data) : 'Error desconocido';
      alert('Error al registrar el pago: ' + errorDetail);
    }
  };

  const [selectedMonths, setSelectedMonths] = useState([]);
  const monthsList = [
    { id: 1, name: 'Enero' }, { id: 2, name: 'Febrero' }, { id: 3, name: 'Marzo' },
    { id: 4, name: 'Abril' }, { id: 5, name: 'Mayo' }, { id: 6, name: 'Junio' },
    { id: 7, name: 'Julio' }, { id: 8, name: 'Agosto' }, { id: 9, name: 'Septiembre' },
    { id: 10, name: 'Octubre' }, { id: 11, name: 'Noviembre' }, { id: 12, name: 'Diciembre' }
  ];

  const currentDiplo = diplomaturas.find(d => d.id.toString() === selectedDiploId.toString());
  
  // Use all docentes from the system
  const availableTeachers = allDocentes.map(t => ({ 
    id: t.id, 
    name: t.first_name || t.last_name ? `${t.first_name} ${t.last_name}` : t.email, 
    email: t.email 
  }));

  const handleMonthToggle = (monthId) => {
    setSelectedMonths(prev => 
      prev.includes(monthId) ? prev.filter(id => id !== monthId) : [...prev, monthId]
    );
  };

  const coordinatorTotal = (selectedMonths.length * rates.monthly_coordinator_rate).toLocaleString();

  return (
    <div className={`modal-overlay ${isFullscreen ? 'p-0' : ''}`} onClick={onClose}>
      <div 
        className={`modal-content glassmorphism manage-permissions-modal ${isFullscreen ? 'modal-expanded mw-100 vh-100 m-0' : ''}`} 
        onClick={e => e.stopPropagation()}
        style={isFullscreen ? { borderRadius: 0 } : {}}
      >
        <div className="modal-header d-flex justify-content-between align-items-center mb-4">
          <h2 className="m-0 d-flex align-items-center gap-2">
            <DollarSign className="text-success" /> Configuración de Salarios y Honorarios
          </h2>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <button
                type="button"
                className="btn-glass p-1 d-flex align-items-center justify-content-center"
                onClick={() => setIsFullscreen(f => !f)}
                title={isFullscreen ? 'Contraer' : 'Ampliar a pantalla completa'}
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button
              type="button"
              className="btn-close-premium"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="nav nav-tabs mb-4 px-2">
          <button 
            className={`nav-link ${activeTab === 'config' ? 'active fw-bold' : ''}`} 
            onClick={() => setActiveTab('config')}
          >
            Configuración y Cálculo
          </button>
          <button 
            className={`nav-link ${activeTab === 'history' ? 'active fw-bold' : ''}`} 
            onClick={() => {
              setActiveTab('history');
              fetchHistory();
            }}
          >
            <History size={16} className="me-2" /> Historial de Pagos
          </button>
          <button 
            className={`nav-link ${activeTab === 'viaticos' ? 'active fw-bold' : ''}`} 
            onClick={() => {
              setActiveTab('viaticos');
            }}
          >
            <DollarSign size={16} className="me-1" /> Viáticos
          </button>
        </div>

        {activeTab === 'config' ? (
        <form onSubmit={handleSave}>
          <div className="mb-4">
            <label className="form-label fw-bold">Seleccionar Diplomatura</label>
            <div className="input-group">
              <span className="input-group-text"><BookOpen size={18} /></span>
              <select 
                className="form-select" 
                value={selectedDiploId} 
                onChange={handleDiploChange}
                disabled={loading}
                required
              >
                <option value="">Seleccione una diplomatura...</option>
                {diplomaturas.map(d => (
                  <option key={d.id} value={d.id}>{d.name} ({d.municipio})</option>
                ))}
              </select>
            </div>
          </div>

          {selectedDiploId && (
            <div className="row g-4 mb-4 text-start">
              <div className="col-md-6">
                <div className="card h-100 border-0 shadow-sm p-3 bg-light bg-opacity-50">
                  <label className="form-label fw-bold d-flex align-items-center gap-2">
                    <User size={18} className="text-primary" /> Sueldo Mensual Coordinador
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={rates.monthly_coordinator_rate}
                      onChange={e => setRates({...rates, monthly_coordinator_rate: e.target.value})}
                      step="0.01"
                      min="0"
                      required
                      disabled={userRole !== 'CO'}
                    />
                  </div>
                  <small className="text-muted mt-2">Valor fijo por mes para el coordinador de esta diplomatura.</small>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card h-100 border-0 shadow-sm p-3 bg-light bg-opacity-50">
                  <label className="form-label fw-bold d-flex align-items-center gap-2">
                    <Clock size={18} className="text-info" /> Valor Hora Docente
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={rates.hourly_teacher_rate}
                      onChange={e => setRates({...rates, hourly_teacher_rate: e.target.value})}
                      step="0.01"
                      min="0"
                      required
                      disabled={userRole !== 'CO'}
                    />
                  </div>
                  <small className="text-muted mt-2">Valor por hora de clase para los docentes de esta diplomatura.</small>
                </div>
              </div>
            </div>
          )}

          {selectedDiploId && (
            <div className="card border-0 shadow-sm p-4 bg-success bg-opacity-10 mb-4 animate-fade-in text-start">
              <h6 className="mb-3 fw-bold d-flex align-items-center gap-2 text-success">
                <User size={18} /> Pago a Coordinador: {currentDiplo?.coordinator_name || 'Sin asignar'} ({currentDiplo?.municipio})
              </h6>
              <div className="small text-muted mb-3">Marque los meses a liquidar:</div>
              <div className="row g-2 mb-3">
                {monthsList.map(m => (
                  <div key={m.id} className="col-3 col-md-2">
                    <div 
                      className={`p-1 rounded text-center border cursor-pointer small ${selectedMonths.includes(m.id) ? 'bg-success text-white border-success' : 'bg-white text-muted'}`}
                      onClick={() => handleMonthToggle(m.id)}
                      style={{ transition: 'all 0.2s', fontSize: '0.75rem' }}
                    >
                      {m.name.substring(0, 3)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded border mb-3">
                <div>
                  <div className="small text-muted">Meses seleccionados: {selectedMonths.length}</div>
                  <div className="fw-bold">Total a Coordinador</div>
                </div>
                <div className="h4 mb-0 fw-bold text-success">${coordinatorTotal}</div>
              </div>
              
              {['CO', 'ACO'].includes(userRole) && (
                <button 
                  type="button" 
                  className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
                  disabled={selectedMonths.length === 0 || !currentDiplo?.coordinator}
                  onClick={() => setPaymentModal({
                    type: 'CO',
                    user: currentDiplo.coordinator,
                    userName: currentDiplo.coordinator_name,
                    amount: selectedMonths.length * rates.monthly_coordinator_rate,
                    period: selectedMonths.map(m => monthsList.find(ml => ml.id === m).name).join(', ')
                  })}
                >
                  <DollarSign size={18} /> Registrar Pago Coordinación
                </button>
              )}
            </div>
          )}

          {selectedDiploId && (
            <div className="card border-0 shadow-sm p-4 bg-primary bg-opacity-10 mb-4 animate-fade-in text-start">
              <h6 className="mb-3 fw-bold d-flex align-items-center gap-2">
                <Calculator size={18} className="text-primary" /> Cálculo de Horas Impartidas (Docentes)
              </h6>
              
              <div className="row g-3 mb-4">
                <div className="col-md-5">
                  <label className="form-label small fw-bold text-muted">DOCENTE</label>
                  {!filters.isManual ? (
                    <select 
                      className="form-select form-select-sm" 
                      value={filters.teacher_email}
                      onChange={e => {
                        if (e.target.value === 'manual') {
                          setFilters({...filters, isManual: true, teacher_email: ''});
                        } else {
                          setFilters({...filters, teacher_email: e.target.value});
                        }
                      }}
                    >
                      <option value="">Seleccione un docente...</option>
                      {availableTeachers.map(t => (
                        <option key={t.id} value={t.email}>{t.name} ({t.email})</option>
                      ))}
                      <option value="manual" className="text-primary fw-bold">+ Ingresar correo manualmente...</option>
                    </select>
                  ) : (
                    <div className="input-group input-group-sm animate-fade-in">
                      <input 
                        type="email" 
                        className="form-control" 
                        placeholder="correo@docente.com" 
                        value={filters.manualEmail}
                        onChange={e => setFilters({...filters, manualEmail: e.target.value})}
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button" 
                        onClick={() => setFilters({...filters, isManual: false, manualEmail: ''})}
                      >
                        <X size={14}/>
                      </button>
                    </div>
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-bold text-muted">DESDE</label>
                  <div 
                    className="input-group input-group-sm cursor-pointer"
                    onClick={() => startDateRef.current?.showPicker()}
                  >
                    <span className="input-group-text"><Calendar size={14} /></span>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={filters.start_date}
                      onChange={e => setFilters({...filters, start_date: e.target.value})}
                      ref={startDateRef}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-bold text-muted">HASTA</label>
                  <div 
                    className="input-group input-group-sm cursor-pointer"
                    onClick={() => endDateRef.current?.showPicker()}
                  >
                    <span className="input-group-text"><Calendar size={14} /></span>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={filters.end_date}
                      onChange={e => setFilters({...filters, end_date: e.target.value})}
                      ref={endDateRef}
                    />
                  </div>
                </div>
                <div className="col-md-1 d-flex align-items-end">
                  <button 
                    type="button" 
                    className="btn btn-primary btn-sm w-100"
                    onClick={handleCalculateHours}
                    disabled={calculating}
                    title="Calcular"
                  >
                    {calculating ? '...' : <Calculator size={14} />}
                  </button>
                </div>
              </div>

              {calcResult && (
                <div className="row g-3">
                  <div className="col-sm-4">
                    <div className="bg-white p-2 rounded text-center border">
                      <div className="small text-muted">Total Horas</div>
                      <div className="h5 mb-0 fw-bold">{calcResult.total_hours.toFixed(1)}h</div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="bg-white p-2 rounded text-center border">
                      <div className="small text-muted">Valor Hora</div>
                      <div className="h5 mb-0 fw-bold">${calcResult.hourly_rate}</div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="bg-white p-2 rounded text-center border text-success">
                      <div className="small text-muted">Total Docente</div>
                      <div className="h5 mb-0 fw-bold">${calcResult.total_payment.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="col-12 mt-3">
                    <div className="alert alert-info py-2 px-3 m-0 d-flex justify-content-between align-items-center" style={{ fontSize: '0.85rem' }}>
                      <span>
                        <Pencil size={14} className="me-2" />
                        Se computaron <strong>{calcResult.class_details.length}</strong> clases con asistencia registrada.
                      </span>
                      {['CO', 'ACO'].includes(userRole) && (
                        <button 
                          type="button" 
                          className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                          onClick={() => {
                            const teacher = allDocentes.find(t => t.email === (filters.isManual ? filters.manualEmail : filters.teacher_email));
                            setPaymentModal({
                              type: 'DO',
                              user: teacher?.id,
                              userName: teacher ? (teacher.first_name ? `${teacher.first_name} ${teacher.last_name}` : teacher.email) : (filters.isManual ? filters.manualEmail : filters.teacher_email),
                              amount: calcResult.total_payment,
                              period: `${filters.start_date} al ${filters.end_date}`
                            });
                          }}
                        >
                          <DollarSign size={14} /> Registrar Pago
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!calcResult && (
                <p className="text-muted small m-0 fst-italic">
                  Haga clic en calcular para obtener el total de horas basadas en el calendario y la asistencia.
                </p>
              )}
            </div>
          )}

          {message && (
            <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
              {message.text}
            </div>
          )}

           <div className="d-flex justify-content-between align-items-center gap-2 mt-4">
            <div className="text-muted small italic">
              {userRole !== 'CO' && (
                "Sueldos y Honorarios impuestos por la Coordinadora Operativa"
              )}
            </div>
            {userRole === 'CO' && (
              <button 
                type="submit" 
                className="btn btn-success d-flex align-items-center gap-2 px-4"
                disabled={saving || !selectedDiploId}
              >
                <Save size={18} /> {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            )}
          </div>
        </form>
        ) : activeTab === 'history' ? (
          <div className="payment-history-container text-start animate-fade-in">
            <div className="table-responsive bg-white rounded shadow-sm border p-2">
              <table className="table table-hover premium-table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Diplomatura</th>
                    <th>Destinatario</th>
                    <th>Tipo</th>
                    <th>Periodo</th>
                    <th>Monto</th>
                    <th>Detalle</th>
                    <th>Comprobante</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory ? (
                    <tr><td colSpan="8" className="text-center py-4 text-muted">Cargando historial...</td></tr>
                  ) : paymentHistory.length === 0 ? (
                    <tr><td colSpan="8" className="text-center py-4 text-muted fst-italic">No hay registros de pagos aún.</td></tr>
                  ) : (
                    paymentHistory.map(p => (
                      <tr key={p.id}>
                        <td className="small">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="fw-medium">{p.diploma_name} ({p.diploma_municipio})</td>
                        <td className="small">
                          <div className="fw-bold">{p.user_name}</div>
                          <div className="text-muted">{p.user_email}</div>
                        </td>
                        <td>
                          <span className={`badge ${p.payment_type === 'CO' ? 'bg-success' : p.payment_type === 'DO' ? 'bg-primary' : 'bg-warning text-dark'} bg-opacity-75`}>
                            {p.payment_type_display}
                          </span>
                        </td>
                        <td className="small">{p.period}</td>
                        <td className="fw-bold text-success">${parseFloat(p.amount).toLocaleString()}</td>
                        <td>
                          {p.description ? (
                            <button
                              className="btn btn-outline-secondary btn-sm p-1"
                              title="Ver detalle completo"
                              onClick={() => setDetailModal(p)}
                            >
                              <Eye size={14} />
                            </button>
                          ) : <span className="text-muted small">-</span>}
                        </td>
                        <td>
                          {p.receipt_pdf ? (
                            <a
                              href={p.receipt_pdf.startsWith('http') ? new URL(p.receipt_pdf).pathname : p.receipt_pdf}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-outline-danger btn-sm p-1"
                              title="Ver PDF"
                            >
                              <FileText size={16} />
                            </a>
                          ) : <span className="text-muted small">N/A</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 d-flex justify-content-end">
              <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
            </div>
          </div>
        ) : activeTab === 'viaticos' ? (
          <div className="viaticos-container text-start animate-fade-in">
            <div className="card shadow-sm border-0 p-4 bg-light mb-4 text-start">
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2">
                <DollarSign className="text-primary" /> Registrar Nuevo Viático
              </h5>
              
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Beneficiario</label>
                  <select 
                    className="form-select"
                    value={filters.viaticoUser}
                    onChange={(e) => setFilters({...filters, viaticoUser: e.target.value})}
                  >
                    <option value="">Seleccione un usuario...</option>
                    {availableTeachers.map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.email})</option>
                    ))}
                    {/* Add other staff if needed, but for now teachers/coords are the main ones */}
                  </select>
                </div>
                
                <div className="col-md-3">
                  <label className="form-label small fw-bold">Monto</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input 
                      type="number" 
                      className="form-control" 
                      placeholder="0.00"
                      value={filters.viaticoAmount}
                      onChange={(e) => setFilters({...filters, viaticoAmount: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="col-md-3">
                  <label className="form-label small fw-bold">Periodo</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ej: Abril 2024"
                    value={filters.viaticoPeriod}
                    onChange={(e) => setFilters({...filters, viaticoPeriod: e.target.value})}
                  />
                </div>
                
                <div className="col-12 mt-3">
                  <label className="form-label small fw-bold">Detalle del Viático</label>
                  <textarea 
                    className="form-control" 
                    rows="2" 
                    placeholder="Descripción del gasto (ej: Viaje a sede central, almuerzo docente, etc.)"
                    value={filters.viaticoDetail}
                    onChange={(e) => setFilters({...filters, viaticoDetail: e.target.value})}
                  ></textarea>
                </div>
                
                <div className="col-12 mt-4">
                  <button 
                    className="btn btn-primary d-flex align-items-center gap-2 px-4 shadow-sm"
                    disabled={!filters.viaticoUser || !filters.viaticoAmount || !selectedDiploId}
                    onClick={() => {
                        const user = availableTeachers.find(t => t.id.toString() === filters.viaticoUser.toString());
                        setPaymentModal({
                            type: 'VI',
                            user: filters.viaticoUser,
                            userName: user ? user.name : 'Usuario',
                            amount: parseFloat(filters.viaticoAmount),
                            period: filters.viaticoPeriod || 'Viático',
                            initialDescription: filters.viaticoDetail || ''
                        });
                    }}
                  >
                    <Upload size={18} /> Continuar con Comprobante
                  </button>
                  {!selectedDiploId && <div className="text-danger small mt-2">* Debe seleccionar una diplomatura antes de registrar viáticos.</div>}
                </div>
              </div>
            </div>
            
            <div className="alert alert-warning py-2 small d-flex align-items-center gap-2">
                <Pencil size={14} /> Los viáticos se registrarán en el historial y enviarán una notificación al beneficiario.
            </div>
          </div>
        ) : (
          <div className="text-center py-5 text-muted">Seleccione una opción del menú.</div>
        )}

        {detailModal && (
          <div className="modal-overlay" style={{ zIndex: 2000 }} onClick={() => setDetailModal(null)}>
            <div className="modal-content glassmorphism p-4" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="m-0 d-flex align-items-center gap-2">
                  <Eye size={18} className="text-secondary" /> Detalle del Pago
                </h5>
                <button className="btn-close-premium" onClick={() => setDetailModal(null)}><X size={16} /></button>
              </div>
              <div className="mb-3">
                <div className="text-muted small mb-1">Destinatario</div>
                <div className="fw-bold">{detailModal.user_name}</div>
                <div className="text-muted small">{detailModal.user_email}</div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div className="text-muted small mb-1">Tipo</div>
                  <span className={`badge ${detailModal.payment_type === 'CO' ? 'bg-success' : detailModal.payment_type === 'DO' ? 'bg-primary' : 'bg-warning text-dark'} bg-opacity-75`}>
                    {detailModal.payment_type_display}
                  </span>
                </div>
                <div className="col-6">
                  <div className="text-muted small mb-1">Monto</div>
                  <div className="fw-bold text-success">${parseFloat(detailModal.amount).toLocaleString()}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted small mb-1">Periodo</div>
                  <div className="small">{detailModal.period}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted small mb-1">Fecha</div>
                  <div className="small">{new Date(detailModal.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              {detailModal.description && (
                <div className="mb-3">
                  <div className="text-muted small mb-1">Descripción</div>
                  <div className="bg-light p-3 rounded border small" style={{ whiteSpace: 'pre-wrap' }}>{detailModal.description}</div>
                </div>
              )}
              {detailModal.receipt_pdf && (
                <a
                  href={detailModal.receipt_pdf.startsWith('http') ? new URL(detailModal.receipt_pdf).pathname : detailModal.receipt_pdf}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  <FileText size={16} /> Ver Comprobante
                </a>
              )}
              <button className="btn btn-secondary w-100 mt-2" onClick={() => setDetailModal(null)}>Cerrar</button>
            </div>
          </div>
        )}

        {paymentModal && (
          <div className="modal-overlay" style={{ zIndex: 2000 }}>
            <div className="modal-content glassmorphism p-4" style={{ maxWidth: '450px' }}>
              <h4 className="mb-3 d-flex align-items-center gap-2">
                <DollarSign className="text-success" /> Registrar Pago
              </h4>
              <div className="alert alert-info py-2 small mb-3">
                <strong>{paymentModal.userName}</strong><br/>
                Tipo: {paymentModal.type === 'CO' ? 'Coordinación' : paymentModal.type === 'DO' ? 'Docente' : 'Viáticos'}<br/>
                Monto: <strong>${paymentModal.amount.toLocaleString()}</strong><br/>
                Periodo: {paymentModal.period}
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData();
                fd.append('user', paymentModal.user);
                fd.append('diploma', selectedDiploId);
                fd.append('payment_type', paymentModal.type);
                fd.append('amount', paymentModal.amount);
                fd.append('period', paymentModal.period);
                fd.append('description', e.target.description.value);
                if (e.target.receipt.files[0]) {
                  fd.append('receipt_pdf', e.target.receipt.files[0]);
                }
                await handlePaymentSubmit(fd);
              }}>
                <div className="mb-3 text-start">
                  <label className="form-label small fw-bold">Descripción / Notas</label>
                  <textarea 
                    name="description" 
                    className="form-control" 
                    rows="2" 
                    placeholder="Opcional..."
                    defaultValue={paymentModal.initialDescription || ''}
                  ></textarea>
                </div>
                
                <div className="mb-4 text-start">
                  <label className="form-label small fw-bold">Comprobante PDF</label>
                  <div className="input-group">
                    <span className="input-group-text"><Upload size={16}/></span>
                    <input type="file" name="receipt" className="form-control" accept="application/pdf,image/*" />
                  </div>
                  <small className="text-muted">Suba el comprobante de transferencia o pago.</small>
                </div>
                
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-secondary flex-grow-1" onClick={() => setPaymentModal(null)}>Cancelar</button>
                  <button type="submit" className="btn btn-success flex-grow-1">Guardar Pago</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalarySettings;
